import { inject, Injectable } from '@angular/core';
import { ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddress, getAssociatedTokenAddressSync, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { PublicKey, TransactionMessage, VersionedTransaction } from '@solana/web3.js';

import { environment } from '../../../environments/environment';
import { getParam, SwapStatus } from '../../shared/constants/app-enums';
import { AnalyticsService } from '../analytics.service';
import { PresaleDataService } from '../connection-services/data.service';
import { ReownService } from '../connection-services/reown.service';
import { EvmPurchseService } from '../evm-services/evm-purchase.service';
import { PixelService } from '../pixel.service';
import { SolanaInitialisationService } from './solana-initialise.service';

@Injectable({
    providedIn: 'root',
})
export class SolPurchaseService {
    private readonly evmPurchaseService = inject(EvmPurchseService);
    private readonly solService = inject(SolanaInitialisationService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly dataService = inject(PresaleDataService);
    private readonly pixelService = inject(PixelService);
    private readonly reownService = inject(ReownService);

    async getRequiredInfoAddress() {
        const accountInfo: any = await this.solService.program.account.presaleAccount.fetch(this.solService.presaleAccountAddress[0]); // this.presaleAccountAddress[0] = Public Key for Presale Account Address
        const userInfoAddress = this.solService.web3PublicKey.findProgramAddressSync([this.solService.solWalletPubkey.toBuffer()], this.solService.programId);

        return { userInfoAddress, accountInfo };
    }

    /**
     * this function is swaping the native token with app token
     * @param amount: app token amount
     * @param value: eth token value
     */

    async swapNativeTokens(tokenNo: number, value: number, isBuyStake: boolean = false, isRetry: boolean = false) {
        this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);
        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddressSOL, walletTransactionHash: '' });
        const referralAddress = getParam('refAddressSol') || this.dataService.walletAddressSOL;
        const refPubKey = new PublicKey(referralAddress);
        const { userInfoAddress, accountInfo } = await this.getRequiredInfoAddress();

        const transactionObj = await this.solService.program.transaction.buyToken(new this.solService.BN(tokenNo), {
            accounts: {
                presaleAccount: this.solService.presaleAccountAddress[0],
                stateInfo: this.solService.stateInfoAddress[0],
                userInfo: userInfoAddress[0],
                signer: this.solService.solWalletPubkey, //this.provider.wallet.publicKey,
                // pythPriceFeed: new this.solanaWeb3.this.web3PublicKey(environment.pythPriceFeed),
                paymentWallet: accountInfo.paymentWallet,
                systemProgram: this.solService.systemProgram.programId,
                otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountSol),
                chainlinkFeed: new this.solService.web3PublicKey(environment.sol.chainlinkFeed),
                chainlinkProgram: new this.solService.web3PublicKey(environment.sol.chainlinkProgram),
                refPubkey: refPubKey,
            },
        });
        transactionObj.feePayer = this.solService.solWalletPubkey;
        const { blockhash, lastValidBlockHeight } = await this.solService.connection?.getLatestBlockhash();
        transactionObj.recentBlockhash = blockhash;

        // Generate unique transaction ID for this buy flow
        const txId = this.pixelService.generateTransactionId();

        this.pixelService.sendPixelEvent('buy_event', {
            action: 'initiated',
            tx_id: txId,
            wallet_address: this.dataService.walletAddressSOL,
            wallet_type: 'SOL',
            wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
            token_symbol: environment.tokenName,
            chain: 'SOL',
            chain_id: environment.sol.chainIdInt,
            amount_token_expected: tokenNo,
            amount_usd_estimated: +(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep].tokenUSDT).toFixed(2),
            payment_method: 'SOL',
            type: isBuyStake ? 'buy_and_stake' : 'buy'
        });

        try {
            const solChainId = environment.sol.chainIdInt;
            const solTokenUSDT = this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep]?.tokenUSDT ?? 0;
            const sig = await this._signAndSendWithFallback(transactionObj, { blockhash, lastValidBlockHeight }, isRetry, (broadcastSig: string) => {
                this.analyticsService.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0, '', '', 0, 0, this.dataService.walletAddressSOL, broadcastSig, this.dataService.balanceDataSOl, solChainId, solTokenUSDT);
            });

            if (sig) {
                this.evmPurchaseService.setTransactionStatus(SwapStatus.complete, sig);
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: sig });
                this.vwoData('successfulTransaction', {
                    walletAddress: this.dataService.walletAddress,
                    transactionId: sig,
                    mode: 'CARYPTO',
                    walletName: this.dataService.connectedWalletName,
                    purchaseType: "buy",
                    tokenAmountPurchase: tokenNo,
                    amountSpent: value,
                    cryptoType: "sol",
                    usdAmount: +(+(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2)),
                });
                this.analyticsService.sendBWData('sol', tokenNo, value, this.dataService.walletAddressSOL, sig, solChainId, 'sol', this.dataService.balanceDataSOl, solTokenUSDT);
                this.analyticsService.windowDataLayer('swap', 'swapSuccessful', 3, 1, 0, 'sol', environment.tokenName, value, tokenNo, this.dataService.walletAddressSOL, sig, this.dataService.balanceDataSOl, solChainId, solTokenUSDT);
                const subid = getParam('subid');
                this.analyticsService.postBacks('dep', subid, 'sol', value, this.dataService.walletAddressSOL, sig);
                this.analyticsService.postbackDataLayer('dep', tokenNo, sig, this.dataService.balanceDataSOl, solChainId);
                // Track success (transaction confirmed)
                try {
                    const txDetails = await this.solService.connection.getTransaction(sig, { maxSupportedTransactionVersion: 0 });
                    const blockNumber = txDetails?.slot;
                    const blockTimestamp = txDetails?.blockTime ? new Date(txDetails.blockTime * 1000).toISOString() : new Date().toISOString();

                    this.pixelService.sendPixelEvent('buy_event', {
                        action: 'success',
                        tx_id: txId,
                        wallet_address: this.dataService.walletAddressSOL,
                        wallet_type: 'SOL',
                        wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                        token_symbol: environment.tokenName,
                        chain: 'SOL',
                        chain_id: environment.sol.chainIdInt,
                        amount_token_expected: tokenNo,
                        amount_usd_estimated: value, // Use value directly as it was passed
                        payment_method: 'SOL',
                        tx_hash: sig,
                        block_number: blockNumber,
                        block_timestamp: blockTimestamp,
                        type: isBuyStake ? 'buy_and_stake' : 'buy'
                    });
                } catch {
                    this.pixelService.sendPixelEvent('buy_event', {
                        action: 'success',
                        tx_id: txId,
                        wallet_address: this.dataService.walletAddressSOL,
                        wallet_type: 'SOL',
                        wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                        token_symbol: environment.tokenName,
                        chain: 'SOL',
                        chain_id: environment.sol.chainIdInt,
                        amount_token_expected: tokenNo,
                        amount_usd_estimated: value,
                        payment_method: 'SOL',
                        tx_hash: sig,
                        type: isBuyStake ? 'buy_and_stake' : 'buy'
                    });
                }

            } else {
                this.evmPurchaseService.setTransactionStatus(SwapStatus.failed, sig);
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: sig });
                this.vwoData('failedAndRejectTransaction', {
                    transactionStatus: 'failed',
                    fundSpentAmount: value,
                    tokenAmountPurchase: tokenNo,
                    purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                    tokenPurchaseMethod: 'sol',
                    transactionId: sig,
                    walletAddress: this.dataService.walletAddress,
                    walletType: this.dataService.connectedWalletName,
                });
                // Track buy failed
                this.pixelService.sendPixelEvent('buy_event', {
                    action: 'failed',
                    tx_id: txId,
                    wallet_address: this.dataService.walletAddressSOL,
                    wallet_type: 'SOL',
                    wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                    token_symbol: environment.tokenName,
                    chain: 'SOL',
                    chain_id: environment.sol.chainIdInt,
                    amount_token_expected: tokenNo,
                    amount_usd_estimated: +(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep].tokenUSDT).toFixed(2),
                    payment_method: 'SOL',
                    failure_type: 'transaction_failed',
                    failure_message: 'Transaction failed',
                    type: isBuyStake ? 'buy_and_stake' : 'buy'
                });
            }
        } catch (error) {
            console.error('Error during transaction:', error);
            this.evmPurchaseService.setTransactionStatus(SwapStatus.failed);
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
            this.vwoData('failedAndRejectTransaction', {
                transactionStatus: 'failed',
                fundSpentAmount: value,
                tokenAmountPurchase: tokenNo,
                purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                tokenPurchaseMethod: 'sol',
                transactionId: '',
                walletAddress: this.dataService.walletAddress,
                walletType: this.dataService.connectedWalletName,
            });
            this.pixelService.sendPixelEvent('buy_event', {
                action: 'failed',
                tx_id: txId,
                wallet_address: this.dataService.walletAddressSOL,
                wallet_type: 'SOL',
                wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                token_symbol: environment.tokenName,
                chain: 'SOL',
                chain_id: environment.sol.chainIdInt,
                amount_token_expected: tokenNo,
                amount_usd_estimated: +(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep].tokenUSDT).toFixed(2),
                payment_method: 'SOL',
                failure_type: 'rpc_error',
                failure_message: (error as any)?.message || 'Transaction failed',
                type: isBuyStake ? 'buy_and_stake' : 'buy'
            });
            if (!isRetry) this.swapNativeTokens(tokenNo, value, isBuyStake, true);
        }
    }

    /**
  * this function is checking allowence
  * amount before swaping the usdt token with app token
  * @param amount: app token amount
  * @param value: usdt token value
  */



    async buyWithStableToken(tokenNo: number, value: number, isUsdc: boolean, isBuyStake: boolean = false, isRetry = false) {
        this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);
        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddressSOL, walletTransactionHash: '' });
        // Generate unique transaction ID
        const txId = this.pixelService.generateTransactionId();
        const paymentMethod = isUsdc ? 'usdc' : 'usdt';

        // Track buy initiated
        this.pixelService.sendPixelEvent('buy_event', {
            action: 'initiated',
            tx_id: txId,
            wallet_address: this.dataService.walletAddressSOL,
            wallet_type: 'SOL',
            wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
            token_symbol: environment.tokenName,
            chain: 'SOL',
            chain_id: environment.sol.chainIdInt,
            amount_token_expected: tokenNo,
            amount_usd_estimated: value,
            payment_method: paymentMethod,
            type: isBuyStake ? 'buy_and_stake' : 'buy'
        });
        const referralAddress = getParam('refAddressSol') || this.dataService.walletAddressSOL;
        const refPubKey = new PublicKey(referralAddress);

        let transactionObj;
        const { userInfoAddress, accountInfo } = await this.getRequiredInfoAddress();
        if (isUsdc) {
            const usdcTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDC_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            const refUsdcPubkey = await getAssociatedTokenAddress(this.solService.USDC_TOKEN_ADDRESS, refPubKey);
            transactionObj = await this.solService.program.transaction.buyWithUsdc(new this.solService.BN(tokenNo), {
                accounts: {
                    presaleAccount: this.solService.presaleAccountAddress[0],
                    stateInfo: this.solService.stateInfoAddress[0],
                    userInfo: userInfoAddress[0],
                    signer: this.solService.solWalletPubkey,
                    paymentWalletUsdcAccount: accountInfo.paymentWalletUsdcAccount,
                    userUsdcAccount: usdcTokenAccount,
                    tokenProgram: new this.solService.web3PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'),
                    systemProgram: this.solService.systemProgram.programId,
                    otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountUsdc),
                    refUsdcPubkey: refUsdcPubkey,
                    usdcMint: new PublicKey(environment.sol.usdcTokenAddress),
                    refPubkey: refPubKey,
                    associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID
                },
            });
        } else {
            const usdtTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDT_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            const refUsdtPubkey = await getAssociatedTokenAddress(this.solService.USDT_TOKEN_ADDRESS, refPubKey);
            transactionObj = await this.solService.program.transaction.buyWithUsdt(new this.solService.BN(tokenNo), {
                accounts: {
                    presaleAccount: this.solService.presaleAccountAddress[0],
                    stateInfo: this.solService.stateInfoAddress[0],
                    userInfo: userInfoAddress[0],
                    signer: this.solService.solWalletPubkey,
                    paymentWalletUsdtAccount: accountInfo.paymentWalletUsdtAccount,
                    userUsdtAccount: usdtTokenAccount,
                    tokenProgram: new this.solService.web3PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'),
                    systemProgram: this.solService.systemProgram.programId,
                    otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountUsdt),
                    refUsdtPubkey: refUsdtPubkey,
                    usdtMint: new PublicKey(environment.sol.usdtTokenAddress),
                    refPubkey: refPubKey,
                    associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID
                },
            });
        }
        transactionObj.feePayer = this.solService.solWalletPubkey;
        const { blockhash, lastValidBlockHeight } = await this.solService.connection?.getLatestBlockhash();
        transactionObj.recentBlockhash = blockhash
        try {
            const solChainId = environment.sol.chainIdInt;
            const solTokenUSDT = this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep]?.tokenUSDT ?? 0;
            const stableMode = isUsdc ? 'usdc' : 'usdt';
            // ---- SIGN & SEND (with fallback) ----
            const sig = await this._signAndSendWithFallback(transactionObj, { blockhash, lastValidBlockHeight }, isRetry, (broadcastSig: string) => {
                this.analyticsService.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0, '', '', 0, 0, this.dataService.walletAddressSOL, broadcastSig, this.dataService.balanceDataSOl, solChainId, solTokenUSDT);
            });
            this.evmPurchaseService.setTransactionStatus(SwapStatus.complete, sig);
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: sig });
            this.vwoData('successfulTransaction', {
                walletAddress: this.dataService.walletAddress,
                transactionId: sig,
                mode: 'CARYPTO',
                walletName: this.dataService.connectedWalletName,
                purchaseType: "buy",
                tokenAmountPurchase: tokenNo,
                amountSpent: value,
                cryptoType: isUsdc ? "sol_usdc" : "sol_usdt",
                usdAmount: +value
            });
            this.analyticsService.sendBWData(stableMode, tokenNo, value, this.dataService.walletAddressSOL, sig, solChainId, stableMode, this.dataService.balanceDataSOl, solTokenUSDT);
            this.analyticsService.windowDataLayer('swap', 'swapSuccessful', 3, 1, 0, stableMode, environment.tokenName, value, tokenNo, this.dataService.walletAddressSOL, sig, this.dataService.balanceDataSOl, solChainId, solTokenUSDT);
            const subid = getParam('subid');
            this.analyticsService.postBacks('dep', subid, stableMode, value, this.dataService.walletAddressSOL, sig);
            this.analyticsService.postbackDataLayer('dep', tokenNo, sig, this.dataService.balanceDataSOl, solChainId);
            // Track success
            try {
                const txDetails = await this.solService.connection.getTransaction(sig, { maxSupportedTransactionVersion: 0 });
                const blockNumber = txDetails?.slot;
                const blockTimestamp = txDetails?.blockTime ? new Date(txDetails.blockTime * 1000).toISOString() : new Date().toISOString();

                this.pixelService.sendPixelEvent('buy_event', {
                    action: 'success',
                    tx_id: txId,
                    wallet_address: this.dataService.walletAddressSOL,
                    wallet_type: 'SOL',
                    wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                    token_symbol: environment.tokenName,
                    chain: 'SOL',
                    chain_id: environment.sol.chainIdInt,
                    amount_token_expected: tokenNo,
                    amount_usd_estimated: value,
                    payment_method: isUsdc ? 'usdc' : 'usdt',
                    tx_hash: sig,
                    block_number: blockNumber,
                    block_timestamp: blockTimestamp,
                    type: isBuyStake ? 'buy_and_stake' : 'buy'
                });
            } catch (err) {
                console.error('Error fetching transaction details:', err);
                this.pixelService.sendPixelEvent('buy_event', {
                    action: 'success',
                    tx_id: txId,
                    wallet_address: this.dataService.walletAddressSOL,
                    wallet_type: 'SOL',
                    wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                    token_symbol: environment.tokenName,
                    chain: 'SOL',
                    chain_id: environment.sol.chainIdInt,
                    amount_token_expected: tokenNo,
                    amount_usd_estimated: value,
                    payment_method: isUsdc ? 'usdc' : 'usdt',
                    tx_hash: sig,
                    type: isBuyStake ? 'buy_and_stake' : 'buy'
                });
            }
        } catch (error) {
            console.error('Error fetching transaction details:', error);
            this.evmPurchaseService.setTransactionStatus(SwapStatus.failed);
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
            this.vwoData('failedAndRejectTransaction', {
                transactionStatus: 'failed',
                fundSpentAmount: value,
                tokenAmountPurchase: tokenNo,
                purchaseType: 'buy',
                tokenPurchaseMethod: 'sol',
                transactionId: '',
                walletAddress: this.dataService.walletAddress,
                walletType: this.dataService.connectedWalletName,
            });
            this.pixelService.sendPixelEvent('buy_event', {
                action: 'failed',
                tx_id: txId,
                wallet_address: this.dataService.walletAddressSOL,
                wallet_type: 'SOL',
                wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                token_symbol: environment.tokenName,
                chain: 'SOL',
                chain_id: environment.sol.chainIdInt,
                amount_token_expected: tokenNo,
                amount_usd_estimated: +(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep].tokenUSDT).toFixed(2),
                payment_method: paymentMethod,
                failure_type: 'rpc_error',
                failure_message: (error as any)?.message || 'Transaction failed',
                type: isBuyStake ? 'buy_and_stake' : 'buy'
            });
            if (!isRetry) this.buyWithStableToken(tokenNo, value, isUsdc, isBuyStake, true);
        }
    }

    /**
     * Try wallet.signAndSendTransaction if available.
     * Otherwise: signTransaction -> sendRawTransaction -> confirmTransaction.
     */
    private async _signAndSendWithFallback(tx: any, bh: { blockhash: string; lastValidBlockHeight: number }, isRetry = false, onBroadcast?: (signature: string) => void): Promise<string> {
        const wallet: any = this.solService.provider?.wallet;
        if (!wallet) throw new Error('Wallet not initialized');
        this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);

        const messageV0 = new TransactionMessage({
            payerKey: this.solService.solWalletPubkey,
            recentBlockhash: bh.blockhash,
            instructions: tx.instructions,
        }).compileToV0Message();

        const versionedTx = new VersionedTransaction(messageV0);
        // 1) Wallet one-shot (many WC wallets DON'T support this)
        if (typeof wallet.signAndSendTransaction === 'function' && !isRetry) {
            const res = await wallet.signAndSendTransaction(versionedTx);
            const signature: string = res?.signature ?? res; // some wallets return string directly
            if (!signature) throw new Error('No signature returned from signAndSendTransaction');

            onBroadcast?.(signature);
            await this.solService.connection?.confirmTransaction({ signature, ...bh }, 'confirmed');
            return signature;
        }

        // 2) Fallback: sign, then send
        if (typeof wallet.signTransaction !== 'function') {
            throw new Error('Wallet does not support signTransaction');
        }

        const signed = await wallet.signTransaction(versionedTx);
        const raw = signed.serialize();

        const signature = await this.solService.connection?.sendRawTransaction(raw, {
            skipPreflight: false,
            maxRetries: 5,
            preflightCommitment: 'confirmed',
        });

        onBroadcast?.(signature);
        await this.solService.connection?.confirmTransaction({ signature, ...bh }, 'confirmed');
        return signature;
    }

    vwoData(eventName: string, eventParams: any, hasParam = true) {
        this.analyticsService.vwoData(eventName, eventParams, hasParam);
    }

    async buyWithWert(tokenNo: number, value: number) {
        this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);


        try {
            const referralAddress = getParam('refAddressSol') || this.dataService.walletAddressSOL;
            const refPubKey = new PublicKey(referralAddress);
            const { userInfoAddress, accountInfo } = await this.getRequiredInfoAddress();
            const ix = await this.solService.program.methods
                .buyTokenWert(new this.solService.BN(tokenNo), new this.solService.BN(+(value * this.solService.lamports_per_sol).toFixed(0)))
                .accounts({
                    presaleAccount: this.solService.presaleAccountAddress[0],
                    stateInfo: this.solService.stateInfoAddress[0],
                    userInfo: userInfoAddress[0],
                    signer: new this.solService.web3PublicKey(environment.wertSigner),
                    user: this.solService.solWalletPubkey,
                    paymentWallet: accountInfo.paymentWallet,
                    systemProgram: this.solService.systemProgram.programId,
                    otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountSol),
                    chainlinkFeed: new this.solService.web3PublicKey(environment.sol.chainlinkFeed),
                    chainlinkProgram: new this.solService.web3PublicKey(environment.sol.chainlinkProgram),
                    refPubkey: refPubKey,
                })
                .instruction();

            // const response = await this.program.methods
            //   .buyTokenWert(new this.solService.BN(tokenNo), new this.solService.BN(+(value * this.lamports_per_sol).toFixed(0)))
            //   .accounts({
            // presaleAccount: this.solService.presaleAccountAddress[0],
            // stateInfo: this.solService.stateInfoAddress[0],
            // userInfo: userInfoAddress[0],
            // signer: new this.solService.web3PublicKey(environment.wertSigner),
            // user: this.solService.solWalletPubkey,
            // paymentWallet: accountInfo.paymentWallet,
            // systemProgram: this.solService.systemProgram.programId,
            // otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountSol),
            // chainlinkFeed: new this.solService.web3PublicKey(environment.sol.chainlinkFeed),
            // chainlinkProgram: new this.solService.web3PublicKey(environment.sol.chainlinkProgram),
            // })
            // .transaction();

            // response.feePayer = new this.web3PublicKey(environment.wertSigner);

            // const recentBlockHash = await (
            //   await this.connection.getLatestBlockhash()
            // ).blockhash;


            const wertPayload = {
                program_id: ix.programId,
                accounts: ix.keys.map((k: any) => ({
                    address: k.pubkey,
                    is_signer: k.isSigner,
                    is_writable: k.isWritable,
                })),
                data: Buffer.from(ix.data).toString('hex'),
            };
            return Buffer.from(JSON.stringify(wertPayload)).toString('hex');
        } catch {
            this.evmPurchaseService.setTransactionStatus(SwapStatus.failed);
            return '';
        }
    }
}